// src/pages/Vouchers/VoucherPrintDialog.jsx
// طباعة السند بقوالب مصمم الطباعة — اختيار القالب (يبدأ بالافتراضي ★) + طباعة + PDF + مشاركة
import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog, DialogContent, DialogActions, Box, Typography, Button, CircularProgress, IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import StarIcon from '@mui/icons-material/Star';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useReactToPrint } from 'react-to-print';
import { toast } from 'react-toastify';
import PrintLayoutRenderer from '../../components/PrintDesigner/PrintLayoutRenderer';
import { loadVoucherForPrint, useInvoiceTemplates, invoicePdf, sharePdfBlob } from '../../components/PrintDesigner/printData';
import { PAPER, paperLabel } from '../../components/PrintDesigner/printTemplates';

const ACCENT = '#4F46E5';
const PX_PER_MM = 96 / 25.4;
const PREVIEW_W = 380;

export default function VoucherPrintDialog({ open, onClose, voucher, companyInfo }) {
  const [state, setState] = useState({ loading: true, data: null, layout: null });

  useEffect(() => {
    if (!open || !voucher) return;
    let alive = true;
    setState({ loading: true, data: null, layout: null });
    loadVoucherForPrint(voucher, companyInfo)
      .then(r => { if (alive) setState({ loading: false, ...r }); })
      .catch(() => { if (alive) setState({ loading: false, data: { ...voucher, company: companyInfo || {} }, layout: null }); });
    return () => { alive = false; };
  }, [open, voucher, companyInfo]);

  return (
    <Dialog open={!!open && !!voucher} onClose={onClose} dir="rtl" maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: '18px', overflow: 'hidden', m: { xs: 1, sm: 4 } } }}>
      {state.loading || !state.data ? (
        <Box sx={{ display: 'grid', placeItems: 'center', gap: 1, py: 8 }}>
          <CircularProgress sx={{ color: ACCENT }} />
          <Typography sx={{ color: '#64748b' }}>جاري تجهيز السند...</Typography>
        </Box>
      ) : (
        <Body key={voucher?.id} data={state.data} layout={state.layout} onClose={onClose} />
      )}
    </Dialog>
  );
}

function Body({ data, layout, onClose }) {
  const { options, current, pick } = useInvoiceTemplates(layout, Number(data.voucherType));
  const pageRef = useRef(null);
  const [busy, setBusy] = useState(null);
  const number = data.voucherNumber || '—';

  const paper = PAPER[current?.layout?.page?.size] || PAPER.A4;
  const scale = Math.min(1, PREVIEW_W / (paper.w * PX_PER_MM));

  const handlePrint = useReactToPrint({
    contentRef: pageRef,
    content: () => pageRef.current,
    documentTitle: `سند-${number}`,
  });

  const run = async (kind) => {
    if (!pageRef.current || !current) return;
    setBusy(kind);
    try {
      const worker = invoicePdf(pageRef.current, current, { invoiceNumber: `سند-${number}` });
      if (kind === 'pdf') await worker.save();
      else {
        const blob = await worker.output('blob');
        const shared = await sharePdfBlob(blob, number);
        if (!shared) toast.info('تم تحميل الملف لأن المشاركة غير مدعومة في هذا المتصفح');
      }
    } catch (e) {
      if (e?.name !== 'AbortError') toast.error(kind === 'pdf' ? 'تعذّر إنشاء ملف PDF' : 'فشل مشاركة السند');
    } finally {
      setBusy(null);
    }
  };

  const btnSx = { borderRadius: '10px', fontWeight: 700, px: 2.25, '& .MuiButton-startIcon': { ml: .75, mr: -.25 } };

  return (
    <>
      {/* الرأس */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2.5, py: 1.5, color: '#fff',
        background: 'linear-gradient(120deg, #4338ca 0%, #4f46e5 45%, #6366f1 100%)' }}>
        <Box sx={{ width: 36, height: 36, borderRadius: '10px', display: 'grid', placeItems: 'center', bgcolor: 'rgba(255,255,255,.16)', border: '1px solid rgba(255,255,255,.28)' }}>
          <ReceiptLongOutlinedIcon fontSize="small" />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 800, fontSize: 16 }}>طباعة السند</Typography>
          <Typography sx={{ fontSize: 12.5, opacity: .85 }} noWrap>
            {data.voucherTypeName ? `${data.voucherTypeName} · ` : ''}رقم {number}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: '#fff' }} aria-label="إغلاق"><CloseIcon /></IconButton>
      </Box>

      <DialogContent sx={{ p: 0, display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0,1fr) 420px' }, minHeight: 440 }}>
        {/* القوالب */}
        <Box sx={{ p: 2, borderInlineEnd: { md: '1px solid #eef0f4' } }}>
          <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#64748b', mb: 1 }}>اختر القالب</Typography>
          <Box sx={{ display: 'grid', gap: 1 }}>
            {options.map(o => {
              const on = o.id === current?.id;
              return (
                <Box key={o.id} role="button" tabIndex={0} onClick={() => pick(o.id)}
                  onKeyDown={e => { if (e.key === 'Enter') pick(o.id); }}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1.25, p: 1.25, borderRadius: '12px', cursor: 'pointer',
                    border: `1.5px solid ${on ? ACCENT : '#e5e7eb'}`, bgcolor: on ? '#eef2ff' : '#fff', '&:hover': { borderColor: ACCENT },
                  }}>
                  {on ? <CheckCircleIcon sx={{ color: ACCENT }} /> : <Box sx={{ width: 24, height: 24, borderRadius: '50%', border: '2px solid #cbd5e1', flexShrink: 0 }} />}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 800, fontSize: 14, color: on ? ACCENT : '#1e293b' }} noWrap>{o.name}</Typography>
                    <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>{paperLabel(o.layout)}</Typography>
                  </Box>
                  {o.isDefault && (
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: .4, px: 1, py: .25, borderRadius: '999px', bgcolor: '#fef3c7', color: '#92400e', fontSize: 11.5, fontWeight: 800 }}>
                      <StarIcon sx={{ fontSize: 14, color: '#f59e0b' }} /> افتراضي
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
          <Typography sx={{ fontSize: 12, color: '#94a3b8', mt: 1.5, lineHeight: 1.7 }}>
            لتعديل القوالب أو إضافة قالب جديد: مصمم الطباعة ← السندات
          </Typography>
        </Box>

        {/* المعاينة */}
        <Box sx={{ bgcolor: '#eef0f4', p: 2, display: 'flex', justifyContent: 'center', overflow: 'auto', maxHeight: { md: '64vh' } }}>
          {current && (
            <Box sx={{ width: paper.w * PX_PER_MM * scale, height: 'fit-content', flexShrink: 0, boxShadow: '0 8px 24px -12px rgba(15,23,42,.4)', bgcolor: '#fff', overflow: 'hidden' }}>
              <Box sx={{ zoom: scale }}>
                <PrintLayoutRenderer key={current.id} layout={current.layout} data={data} />
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.5, gap: 1, borderTop: '1px solid #eef0f4', flexWrap: 'wrap' }}>
        <Button variant="contained" startIcon={<PrintOutlinedIcon />} onClick={handlePrint} disabled={!!busy}
          sx={{ ...btnSx, bgcolor: ACCENT, '&:hover': { bgcolor: '#4338CA' } }}>طباعة</Button>
        <Button variant="outlined" onClick={() => run('pdf')} disabled={!!busy}
          startIcon={busy === 'pdf' ? <CircularProgress size={16} /> : <PictureAsPdfOutlinedIcon />}
          sx={{ ...btnSx, color: ACCENT, borderColor: '#c7d2fe' }}>تحميل PDF</Button>
        <Button variant="outlined" onClick={() => run('share')} disabled={!!busy}
          startIcon={busy === 'share' ? <CircularProgress size={16} /> : <ShareOutlinedIcon />}
          sx={{ ...btnSx, color: ACCENT, borderColor: '#c7d2fe' }}>مشاركة</Button>
        <Box sx={{ flex: 1 }} />
        <Button variant="text" onClick={onClose} sx={{ ...btnSx, color: '#64748b', bgcolor: 'transparent', '&:hover': { bgcolor: '#f1f5f9' } }}>إغلاق</Button>
      </DialogActions>

      {/* نسخة بالحجم الحقيقي خارج الشاشة — منها الطباعة والـ PDF */}
      <Box aria-hidden sx={{ position: 'fixed', left: -10000, top: 0, pointerEvents: 'none' }}>
        {current && <PrintLayoutRenderer key={`real-${current.id}`} ref={pageRef} layout={current.layout} data={data} />}
      </Box>
    </>
  );
}